import React, { ComponentProps, useCallback, useMemo } from 'react'

import TextButton from 'LuxKit/Button/TextButton'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import offerPageURL from 'lib/offer/offerPageURL'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { BOOKING_DETAILS_COPY, VIEW_OFFER_COPY } from 'tripPlanner/config'
import useOffer from 'hooks/Offers/useOffer'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import { TripItem } from 'tripPlanner/types/tripItem'
import {
  buildMyEscapesOrderLink,
  isLeBooking,
  isLeItem,
} from 'tripPlanner/utils'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'

interface Props {
  item: TripItem
  size?: ComponentProps<typeof TextButton>['size']
}

export default function BookedOfferDetailsCTA({
  item,
  size = 'medium',
}: Props) {
  const canEdit = useCanEditTrip()
  const currentUserId = useAppSelector(getCurrentUserId)
  const isItemOwner = currentUserId === item.createdBy
  const [offer] = useOffer('code' in item ? item.code : undefined)
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const modalElement = useModalElementContext()

  const offerLink = useMemo(() => {
    if (!offer) return undefined
    return offerPageURL(offer)
  }, [offer])
  const viewOffer = useCallback(() => {
    if (isLeItem(item) && offerLink) {
      setImmersiveMode({
        sectionId: tripItemSelectionId(item.id),
        tripId: item.tripId,
      })
      modalElement.resolve()
    }
  }, [item, offerLink, setImmersiveMode, modalElement])

  const myEscapesLink =
    canEdit ? buildMyEscapesOrderLink(item) : undefined
  const viewBooking = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  if (!isLeBooking(item)) return null

  if (isItemOwner && myEscapesLink) {
    return (
      <TextButton
        kind="secondary"
        size={size}
        variant="default"
        to={myEscapesLink}
        onClick={viewBooking}
      >
        {BOOKING_DETAILS_COPY}
      </TextButton>
    )
  } else if (offerLink) {
    return (
      <TextButton
        kind="secondary"
        size={size}
        variant="default"
        to={offerLink}
        target="_blank"
        onClick={viewOffer}
      >
        {VIEW_OFFER_COPY}
      </TextButton>
    )
  } else {
    return null
  }
}
